/**
 * Shared route utilities
 *
 * NOTE: URLs no longer contain role prefixes (e.g., /platform-admin/, /agency-admin/)
 * The layout variant is determined from the user's role in AppRoutes, not from the URL
 */

import {isEnabled} from '~/src/featureFlags'
import {hasModulePermission as hasRouteModulePermission} from '~/src/routes/hasModulePermission'
import {UserRole} from './permissions'
import {
  platformAdminList,
  agencyAdminList,
  companyAdminList,
  mspAgencyList,
  generalAdminList,
  mainAgencyAdminList,
  type INavMenuItem,
} from '~/src/components/layout/MenuItems'

/**
 * Module permission interface for permission checking
 */
export interface ModulePermission {
  key: string
  name: string
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  submodules?: ModulePermission[]
}

/**
 * Module key aliases for role-specific permission mappings
 * Some menu items use a generic moduleKey but specific roles have different keys
 */
const MODULE_KEY_ALIASES: Record<string, string[]> = {
  users_roles: ['company_users_roles', 'agency_users_roles', 'agency-users'],
  positions: ['company_positions'],
  // Agency category permissions from BE use "agency-*" prefixed keys
  // Map standard menu moduleKeys to their agency-prefixed equivalents
  dashboard: ['agency-company-management'],
  manage_companies: ['agency-company-management', 'company_manage_profile'],
  manage_orders: ['agency-job-orders', 'agency-assignments', 'company_manage_orders'],
  candidates: ['agency-candidates'],
  manage_profile: ['agency-billing', 'agency-reports'],
  pre_fill: ['agency-job-orders'],
  // Company category aliases
  service_orders: ['company_orders', 'agency_orders'],
}

/**
 * Maps a role name or category to its corresponding menu items list
 * Matches the logic in AppRoutes.tsx getVariantFromRole
 * Supports both role names (e.g., 'platformadmin') and categories (e.g., 'Platform', 'MSP')
 */
export const getMenuItemsForRole = (roleNameOrCategory: string): INavMenuItem[] => {
  const lower = roleNameOrCategory.toLowerCase()

  // Check for role categories first (for custom roles)
  if (lower === 'platform') {
    return platformAdminList
  }
  if (lower === 'msp') {
    return mspAgencyList
  }
  if (lower === 'agency') {
    return agencyAdminList
  }
  if (lower === 'company') {
    return companyAdminList
  }

  // Then check for specific role names (for built-in roles)
  if (lower === 'platformadmin') {
    return platformAdminList
  }
  if (lower === 'general_admin') {
    return generalAdminList
  }
  if (lower === 'mainagencyadmin') {
    return mainAgencyAdminList
  }
  if (lower === 'mspagencyadmin' || lower === 'mspacencyadmin') {
    return mspAgencyList
  }
  if (lower === 'agencyadmin' || lower.includes('agency')) {
    return agencyAdminList
  }
  if (lower === 'companyadmin' || lower === 'companyuser') {
    return companyAdminList
  }

  // Default to platform admin list
  return platformAdminList
}

/**
 * Helper to check if a key exists in any module's submodules with canView permission
 */
const hasSubmodulePermission = (
  modulePermissions: ModulePermission[],
  targetKey: string,
): boolean => {
  for (const mod of modulePermissions) {
    if (!mod.submodules || mod.submodules.length === 0) continue
    const submodule = mod.submodules.find((s) => s.key === targetKey)
    if (submodule?.canView) {
      return true
    }
  }
  return false
}

/**
 * Checks if user has permission for a moduleKey
 * Supports checking in submodules and module key aliases
 */
export const hasModulePermission = (
  modulePermissions: ModulePermission[],
  moduleKey: string,
): boolean => {
  // Check direct module match
  const module = modulePermissions.find((m) => m.key === moduleKey)
  if (module) {
    if (module.canView) {
      return true
    }
    // Submodule canView fallback: allow access if any submodule has canView: true
    if (module.submodules?.some((s) => s.canView)) {
      return true
    }
  }

  // Check if moduleKey exists as a submodule anywhere
  if (hasSubmodulePermission(modulePermissions, moduleKey)) {
    return true
  }

  // Check module key aliases
  const aliases = MODULE_KEY_ALIASES[moduleKey]
  if (!aliases || aliases.length === 0) {
    return false
  }

  for (const aliasKey of aliases) {
    // Check alias as direct module
    const aliasModule = modulePermissions.find((m) => m.key === aliasKey)
    if (aliasModule) {
      if (aliasModule.canView) {
        return true
      }
      if (aliasModule.submodules?.some((s) => s.canView)) {
        return true
      }
    }
    // Check alias in submodules
    if (hasSubmodulePermission(modulePermissions, aliasKey)) {
      return true
    }
  }

  return false
}

function isPlatformAdminRole(roleNameOrCategory: string): boolean {
  const lower = roleNameOrCategory.toLowerCase()
  return (
    lower === 'platform' ||
    lower === 'platformadmin' ||
    lower === 'platform administrator' ||
    lower.startsWith('platform ')
  )
}

/** Omits dashboard from landing nav when platform metrics flag is off. */
function getLandingMenuItems(roleName: string): INavMenuItem[] {
  const menuItems = getMenuItemsForRole(roleName)
  if (!isPlatformAdminRole(roleName) || isEnabled('dashboardMetrics')) {
    return menuItems
  }
  return menuItems.filter((item) => item.moduleKey !== 'dashboard')
}

/**
 * STFFLG-4163: an item gated on a submodule (My Signatures: settings > esign_signing). Asked the
 * way the route guard asks it, so a landing route is never one the guard then sends to
 * /unauthorized: without this, a user whose only grant was elsewhere under Settings would land on
 * /my-signatures by way of the module-level check below.
 */
function passesItemGate(item: INavMenuItem, modulePermissions: ModulePermission[]): boolean {
  if (item.moduleKey && item.submoduleKey) {
    return hasRouteModulePermission(modulePermissions, item.moduleKey, item.submoduleKey) === true
  }
  return true
}

/**
 * Finds the first accessible route from menu items based on user permissions
 * Handles nested subMenuItems and returns the first link the user can view
 */
export const findFirstAccessibleRoute = (
  menuItems: INavMenuItem[],
  modulePermissions: ModulePermission[],
): string | null => {
  for (const item of menuItems) {
    // Check if user has permission for this menu item
    if (item.moduleKey && !hasModulePermission(modulePermissions, item.moduleKey)) {
      continue
    }
    if (!passesItemGate(item, modulePermissions)) {
      continue
    }

    // If this item has a direct link, return it
    if (item.link) {
      return item.link
    }

    // If this item has subMenuItems, find the first accessible submenu link
    if (item.subMenuItems && item.subMenuItems.length > 0) {
      for (const subItem of item.subMenuItems) {
        // Check submenu permission
        if (subItem.moduleKey && !hasModulePermission(modulePermissions, subItem.moduleKey)) {
          continue
        }
        if (subItem.link) {
          return subItem.link
        }
      }
    }
  }

  return null
}

/**
 * Get the landing route for a role based on permissions
 * Priority: first accessible menu item the user has permission to view
 * URLs no longer contain role prefixes - all routes are at root level
 */
export const getRouteForRole = (
  roleName: string,
  modulePermissions?: ModulePermission[],
): string => {
  // If no permissions data, fall back to role-based defaults
  if (!modulePermissions || modulePermissions.length === 0) {
    const lowerRoleName = roleName.toLowerCase()
    if (lowerRoleName === 'candidate') {
      return '/dashboard'
    }
    if (lowerRoleName === 'companyadmin' || lowerRoleName === 'companyuser') {
      return '/manage-orders'
    }
    return '/manage-companies'
  }

  const menuItems = getLandingMenuItems(roleName)

  const accessibleRoute = findFirstAccessibleRoute(menuItems, modulePermissions)

  if (accessibleRoute) {
    return accessibleRoute
  }

  // Fallback: if no accessible menu item found, try common routes
  const lowerRoleName = roleName.toLowerCase()
  if (lowerRoleName === 'candidate') {
    return '/dashboard'
  }
  if (lowerRoleName === 'companyadmin' || lowerRoleName === 'companyuser') {
    return '/manage-orders'
  }

  return '/manage-companies'
}

/**
 * Get the appropriate agency management route
 * No role prefix needed - layout variant determined from user role
 */
export const getAgencyManagementRoute = (
  _userRole: UserRole,
  agencyId: string,
  tab?: string,
): string => {
  const baseRoute = `/manage-agencies/${agencyId}`
  return tab ? `${baseRoute}/${tab}` : `${baseRoute}/profile`
}

/**
 * Get the appropriate company management route
 * No role prefix needed - layout variant determined from user role
 */
export const getCompanyManagementRoute = (
  _userRole: UserRole,
  companyId: string,
  tab?: string,
): string => {
  const baseRoute = `/manage-companies/${companyId}`
  return tab ? `${baseRoute}/${tab}` : `${baseRoute}/profile`
}

/**
 * @deprecated No longer needed - URLs don't have role prefixes
 * Kept for backwards compatibility but returns empty string
 */
export const getBaseRouteFromPath = (_pathname: string): string => {
  return ''
}

/**
 * @deprecated No longer needed - URLs don't have role prefixes
 * Determines base route for entity management
 */
export const getBaseRoute = (
  _pathname: string,
  entityType?: 'companies' | 'agencies' | 'catalogs' | 'global',
): string => {
  // Return entity-specific route directly without admin prefix
  if (entityType) {
    switch (entityType) {
      case 'companies':
        return '/manage-companies'
      case 'agencies':
        return '/manage-agencies'
      case 'catalogs':
        return '/global/wc-catalogs'
      case 'global':
        return '/global'
      default:
        return ''
    }
  }

  return ''
}

/**
 * Get company-specific base route with entity hierarchy support
 */
export const getCompanyBaseRoute = (
  _pathname: string,
  companyId: string,
  regionId?: string,
): string => {
  const baseRoute = '/manage-companies'

  if (regionId) {
    return `${baseRoute}/${companyId}/regions/${regionId}`
  }

  return `${baseRoute}/${companyId}`
}

/**
 * Get agency-specific base route
 */
export const getAgencyBaseRoute = (_pathname: string, agencyId: string): string => {
  return `/manage-agencies/${agencyId}`
}

/**
 * Get catalog-specific base route with support for different contexts
 */
export const getCatalogBaseRoute = (
  _pathname: string,
  context: 'global' | 'agency' | 'company',
  entityId?: string,
): string => {
  switch (context) {
    case 'global':
      return '/global/wc-catalogs'
    case 'agency':
      return `/manage-agencies/${entityId}/catalogs`
    case 'company':
      return `/manage-companies/${entityId}/catalogs`
    default:
      return '/global/wc-catalogs'
  }
}

/**
 * @deprecated Determine user role from Redux store instead of URL
 * Kept for backwards compatibility - returns PLATFORM_ADMIN as default
 */
export const getUserRoleFromPath = (_pathname: string): UserRole => {
  // With the new architecture, user role should be determined from Redux store
  // not from the URL path. Return a sensible default.
  return UserRole.PLATFORM_ADMIN
}

/**
 * Check if current user has access to specific functionality based on role
 */
export const hasRoleAccess = (userRole: UserRole, requiredRole: UserRole): boolean => {
  const roleHierarchy = {
    [UserRole.PLATFORM_ADMIN]: 5,
    [UserRole.GENERAL_ADMIN]: 4,
    [UserRole.MSP_ADMIN]: 3,
    [UserRole.AGENCY_ADMIN]: 2,
    [UserRole.COMPANY_ADMIN]: 1,
  }

  return (roleHierarchy[userRole] || 0) >= (roleHierarchy[requiredRole] || 0)
}
